import * as THREE from 'three';
import { BODIES, type BodyDef } from '../data/bodies';
import { auToScene } from '../scale/units';
import {
  loadTexture,
  makeStarfieldTexture,
  makeNebulaSprite,
  getProcedural,
} from '../assets/textures';
import { CelestialBody } from './CelestialBody';

export class SolarSystem {
  root = new THREE.Group();
  bodies: CelestialBody[] = [];
  bodyMap = new Map<string, CelestialBody>();
  sunLight: THREE.PointLight;
  private orbitLines = new THREE.Group();
  private asteroids?: THREE.Points;
  private timeScale = 8; // visual orbital animation speed

  constructor() {
    this.root.add(this.orbitLines);
    this.sunLight = new THREE.PointLight(0xfff2dd, 2.8, 0, 0);
    this.sunLight.position.set(0, 0, 0);
    this.root.add(this.sunLight);

    // Soft fill so night sides aren't pure black
    const amb = new THREE.AmbientLight(0x1a2238, 0.12);
    this.root.add(amb);
    const hemi = new THREE.HemisphereLight(0x223355, 0x050508, 0.18);
    this.root.add(hemi);
  }

  async build(onProgress?: (p: number, label: string) => void): Promise<void> {
    const report = (p: number, label: string) => onProgress?.(p, label);

    report(0.02, 'Charting stars…');
    this.addStarfield();
    this.addNebulae();

    // Load textures for unique keys
    const keys = new Set<string>();
    for (const b of BODIES) {
      if (b.textureKey) keys.add(b.textureKey);
      if (b.cloudKey) keys.add(b.cloudKey);
    }
    const keyList = [...keys];
    const textures = new Map<string, THREE.Texture>();
    let loaded = 0;
    // Load in parallel with per-texture timeouts (see loadTexture). Never block splash forever.
    await Promise.all(
      keyList.map(async (key) => {
        const color =
          BODIES.find((b) => b.textureKey === key || b.cloudKey === key)?.color ??
          '#888';
        try {
          const tex = await loadTexture(key, color);
          textures.set(key, tex);
        } catch (err) {
          console.warn(`[SkyTrek] Skipping texture ${key}`, err);
          textures.set(key, getProcedural(key, color));
        }
        loaded++;
        report(0.05 + (loaded / keyList.length) * 0.55, `Surfaces · ${key}`);
      }),
    );

    report(0.65, 'Placing worlds…');

    // Create all bodies
    for (const def of BODIES) {
      const map = def.textureKey
        ? textures.get(def.textureKey) ?? getProcedural(def.textureKey, def.color)
        : getProcedural(def.id, def.color);
      const clouds = def.cloudKey ? textures.get(def.cloudKey) : undefined;
      const body = new CelestialBody(def, { map, clouds });
      this.bodies.push(body);
      this.bodyMap.set(def.id, body);
      this.root.add(body.group);
    }

    // Wire hierarchy
    for (const body of this.bodies) {
      if (body.def.parent) {
        const parent = this.bodyMap.get(body.def.parent);
        if (parent) body.setParent(parent);
      } else if (body.def.id !== 'sun') {
        body.setHeliocentricOrbit();
      }
    }

    // Initial positions (update once)
    for (const body of this.bodies) {
      body.update(0, 0);
    }
    // Second pass so moons use updated parent positions
    for (const body of this.bodies) {
      body.update(0, 0);
    }

    report(0.8, 'Drawing orbits…');
    this.buildOrbitPaths();

    report(0.9, 'Asteroid belt…');
    this.buildAsteroidBelt();

    // Sun light intensity scale
    const sun = this.bodyMap.get('sun');
    if (sun) {
      this.sunLight.intensity = 3.2;
    }

    report(1, 'Ready');
  }

  private addStarfield(): void {
    const tex = makeStarfieldTexture(2048);
    const geo = new THREE.SphereGeometry(500_000, 64, 64);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      side: THREE.BackSide,
      depthWrite: false,
    });
    const sky = new THREE.Mesh(geo, mat);
    sky.name = 'starfield';
    this.root.add(sky);
  }

  private addNebulae(): void {
    const specs: { color: string; pos: THREE.Vector3; scale: number }[] = [
      {
        color: '#6644aa',
        pos: new THREE.Vector3(auToScene(45), auToScene(8), -auToScene(30)),
        scale: auToScene(12),
      },
      {
        color: '#2244aa',
        pos: new THREE.Vector3(-auToScene(55), -auToScene(5), auToScene(40)),
        scale: auToScene(15),
      },
      {
        color: '#aa3366',
        pos: new THREE.Vector3(auToScene(20), auToScene(12), auToScene(60)),
        scale: auToScene(10),
      },
      {
        color: '#338866',
        pos: new THREE.Vector3(-auToScene(70), auToScene(3), -auToScene(20)),
        scale: auToScene(14),
      },
    ];

    for (const s of specs) {
      const mat = new THREE.SpriteMaterial({
        map: makeNebulaSprite(s.color),
        transparent: true,
        opacity: 0.35,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const sprite = new THREE.Sprite(mat);
      sprite.position.copy(s.pos);
      sprite.scale.setScalar(s.scale);
      this.root.add(sprite);
    }
  }

  private buildOrbitPaths(): void {
    const orbitMaterial = new THREE.LineBasicMaterial({
      color: 0x6a8ab0,
      transparent: true,
      opacity: 0.14,
      depthWrite: false,
    });
    const moonOrbitMat = new THREE.LineBasicMaterial({
      color: 0x8a9aaa,
      transparent: true,
      opacity: 0.1,
      depthWrite: false,
    });

    for (const body of this.bodies) {
      if (body.def.id === 'sun') continue;
      if (body.def.kind === 'moon') continue; // moons: subtle, added relative

      const segs = 256;
      const pts: THREE.Vector3[] = [];
      const a = body.orbitRadiusScene;
      const e = body.def.eccentricity;
      const inc = THREE.MathUtils.degToRad(body.def.inclination);
      for (let i = 0; i <= segs; i++) {
        const theta = (i / segs) * Math.PI * 2;
        const r = (a * (1 - e * e)) / (1 + e * Math.cos(theta));
        const x = Math.cos(theta) * r;
        const z = Math.sin(theta) * r;
        const y = Math.sin(theta) * Math.sin(inc) * r * 0.35;
        pts.push(new THREE.Vector3(x, y, z));
      }
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const line = new THREE.Line(geo, orbitMaterial);
      line.userData.bodyId = body.def.id;
      this.orbitLines.add(line);
    }

    // Moon orbits as small local rings (updated each frame would be heavy; static approx from initial parent)
    for (const body of this.bodies) {
      if (body.def.kind !== 'moon' || !body.parent) continue;
      if (body.orbitRadiusScene < body.parent.radius * 1.5) continue;
      const segs = 96;
      const pts: THREE.Vector3[] = [];
      const a = body.orbitRadiusScene;
      const inc = THREE.MathUtils.degToRad(body.def.inclination);
      for (let i = 0; i <= segs; i++) {
        const theta = (i / segs) * Math.PI * 2;
        pts.push(
          new THREE.Vector3(
            Math.cos(theta) * a,
            Math.sin(theta) * Math.sin(inc) * a * 0.35,
            Math.sin(theta) * a,
          ),
        );
      }
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const line = new THREE.LineLoop(geo, moonOrbitMat);
      line.userData.parentId = body.def.parent;
      line.userData.isMoonOrbit = true;
      // Parent will be tracked in update
      line.position.copy(body.parent.worldPosition);
      this.orbitLines.add(line);
    }
  }

  private buildAsteroidBelt(): void {
    const count = 4500;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const color = new THREE.Color();

    for (let i = 0; i < count; i++) {
      const au = 2.1 + Math.random() * 1.3;
      const theta = Math.random() * Math.PI * 2;
      const r = auToScene(au) * (0.98 + Math.random() * 0.04);
      const y = (Math.random() - 0.5) * auToScene(0.08);
      positions[i * 3] = Math.cos(theta) * r;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = Math.sin(theta) * r;
      const shade = 0.35 + Math.random() * 0.4;
      color.setRGB(shade, shade * 0.95, shade * 0.85);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    // Sparse Kuiper samples
    const kuiper = 1200;
    const allPos = new Float32Array((count + kuiper) * 3);
    const allCol = new Float32Array((count + kuiper) * 3);
    allPos.set(positions);
    allCol.set(colors);
    for (let i = 0; i < kuiper; i++) {
      const au = 35 + Math.random() * 25;
      const theta = Math.random() * Math.PI * 2;
      const r = auToScene(au);
      const y = (Math.random() - 0.5) * auToScene(2);
      const idx = count + i;
      allPos[idx * 3] = Math.cos(theta) * r;
      allPos[idx * 3 + 1] = y;
      allPos[idx * 3 + 2] = Math.sin(theta) * r;
      const shade = 0.4 + Math.random() * 0.35;
      allCol[idx * 3] = shade * 0.9;
      allCol[idx * 3 + 1] = shade * 0.95;
      allCol[idx * 3 + 2] = shade;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(allPos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(allCol, 3));
    const mat = new THREE.PointsMaterial({
      size: 1.8,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    });
    this.asteroids = new THREE.Points(geo, mat);
    this.root.add(this.asteroids);
  }

  update(dt: number): void {
    // Planets first, then moons (parents before children)
    const starsAndPlanets = this.bodies.filter((b) => !b.parent);
    const moons = this.bodies.filter((b) => b.parent);
    for (const b of starsAndPlanets) b.update(dt, this.timeScale);
    for (const b of moons) b.update(dt, this.timeScale);

    // Keep moon orbit guides on parents
    for (const child of this.orbitLines.children) {
      if (child.userData.isMoonOrbit && child.userData.parentId) {
        const p = this.bodyMap.get(child.userData.parentId);
        if (p) child.position.copy(p.worldPosition);
      }
    }
  }

  getBody(id: string): CelestialBody | undefined {
    return this.bodyMap.get(id);
  }

  findNearest(position: THREE.Vector3): { body: CelestialBody; dist: number } | null {
    let best: CelestialBody | null = null;
    let bestD = Infinity;
    for (const b of this.bodies) {
      if (b.def.kind === 'star') continue;
      const d = position.distanceTo(b.worldPosition) - b.radius;
      if (d < bestD) {
        bestD = d;
        best = b;
      }
    }
    if (!best) return null;
    return { body: best, dist: Math.max(0, bestD) };
  }

  /** Bodies whose meshes can block the camera */
  getColliders(): CelestialBody[] {
    return this.bodies;
  }
}
